import { useState, type FormEvent } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AccountsAdminApi } from '../adminApi.js'
import type { AdminClientSummary } from '../../clients/index.js'
import type { AdminOperator, Invitation, InvitationResult, OperatorUpdate } from '../index.js'
import { SecretBanner, Skeleton } from '../../core/index.js'
import { OperatorIcon } from '../../core/index.js'
import { MailPlus, Save, Send, XCircle } from 'lucide-react'
import { InvitationHandOffBanner } from '../components/InviteOperatorModal.js'
import { Button } from '../../core/index.js'
import { LoadFailure, RefreshButton } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = AccountsAdminApi

const INPUT_CLASS = 'border border-marine/20 px-2 py-1.5 text-xs'
const LINK_BUTTON = 'text-[11px] font-bold text-marine hover:text-marine-dk disabled:opacity-40'
const PASSWORD_LABEL = "One-time password — copy it now, it won't be shown again"

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback
}

function AddOperatorForm({ isPending, error, onCreate, onCancel }: { isPending: boolean; error: string | null; onCreate: (input: { email: string; firstName: string; lastName: string }) => void; onCancel: () => void }) {
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')

  function handleSubmit(event: FormEvent): void {
    event.preventDefault()
    onCreate({ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() })
  }

  return (
    <form onSubmit={handleSubmit} className="bg-marine-lt/40 p-3 flex flex-col gap-2 mt-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input aria-label="First name" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={INPUT_CLASS} required autoFocus />
        <input aria-label="Last name" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={INPUT_CLASS} required />
        <input aria-label="Email" type="email" placeholder="Email (their login username)" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT_CLASS} required />
      </div>
      <div className="flex items-center gap-3">
        <Button type="submit" icon={MailPlus} size="sm" disabled={isPending}>
          {isPending ? 'Inviting…' : 'Invite operator'}
        </Button>
        <button type="button" onClick={onCancel} className="text-[11px] font-bold text-ink-mid hover:text-ink">
          Cancel
        </button>
        {error && <span className="text-[11px] text-coral font-bold">{error}</span>}
      </div>
    </form>
  )
}

/** A pending operator's panel: the invitation waits, so there is nothing to rotate - only resend or withdraw. */
function PendingOperatorPanel({ invitation, onResend, onRevoke, isBusy, error }: { invitation: Invitation | null; onResend: (invitationId: string) => void; onRevoke: (invitationId: string) => void; isBusy: boolean; error: string | null }) {
  if (!invitation) {
    return <div className="mt-2 bg-marine-lt/40 p-3 text-[11px] text-ink-mid">Invited; the invitation record could not be found. Remove the operator and invite again.</div>
  }
  return (
    <div className="mt-2 bg-marine-lt/40 p-3 flex flex-wrap items-center gap-3 text-[11px]">
      <span className="text-ink-mid">Invited {new Date(invitation.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} by {invitation.invitedBy.name ?? invitation.invitedBy.sub} · expires {new Date(invitation.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>
      <Button icon={Send} variant="secondary" size="sm" onClick={() => onResend(invitation.invitationId)} disabled={isBusy}>
        Resend
      </Button>
      <Button icon={XCircle} variant="secondary" size="sm" onClick={() => onRevoke(invitation.invitationId)} disabled={isBusy}>
        Withdraw
      </Button>
      {error && <span className="text-coral font-bold">{error}</span>}
    </div>
  )
}

interface ManageOperatorPanelProps {
  api: AdminApi
  registrationId: string
  operator: AdminOperator
  /** True when removing this operator would leave the client with nobody able to sign in. */
  isOnlyLogin: boolean
  onChanged: () => Promise<void>
}

/** Edit, re-credential or remove one operator - the same actions an operator has for a facility administrator. */
function ManageOperatorPanel({ api, registrationId, operator, isOnlyLogin, onChanged }: ManageOperatorPanelProps) {
  const [firstName, setFirstName] = useState(operator.firstName)
  const [lastName, setLastName] = useState(operator.lastName)
  const [email, setEmail] = useState(operator.email)
  const [rotatedPassword, setRotatedPassword] = useState<string | null>(null)
  const [resetLink, setResetLink] = useState<string | null>(null)
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false)

  const updateMutation = useMutation({
    mutationFn: (input: OperatorUpdate) => api.updateOperator(registrationId, operator.operatorId, input),
    onSuccess: async (updated) => {
      if (updated.password) {
        setRotatedPassword(updated.password)
      }
      await onChanged()
    },
  })
  const resetLinkMutation = useMutation({
    mutationFn: () => api.createOperatorResetLink(registrationId, operator.operatorId),
    onSuccess: ({ resetUrl }) => setResetLink(resetUrl),
  })
  const removeMutation = useMutation({
    mutationFn: () => api.deleteOperator(registrationId, operator.operatorId),
    onSuccess: onChanged,
  })

  const isDirty = firstName !== operator.firstName || lastName !== operator.lastName || email !== operator.email
  const canSave = isDirty && firstName.trim().length > 0 && lastName.trim().length > 0 && email.trim().length > 0
  const failure = updateMutation.error ?? resetLinkMutation.error ?? removeMutation.error

  return (
    <div className="bg-marine-lt/40 p-3 flex flex-col gap-2 mt-2">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <input aria-label="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} className={INPUT_CLASS} />
        <input aria-label="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} className={INPUT_CLASS} />
        <input aria-label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={INPUT_CLASS} />
      </div>
      <div className="flex items-center gap-3 flex-wrap">
        <Button
          icon={Save}
          size="sm"
          disabled={!canSave || updateMutation.isPending}
          onClick={() => updateMutation.mutate({ firstName: firstName.trim(), lastName: lastName.trim(), email: email.trim() })}
        >
          Save changes
        </Button>
        <button type="button" disabled={updateMutation.isPending} onClick={() => updateMutation.mutate({ rotatePassword: true })} className={LINK_BUTTON}>
          Rotate password
        </button>
        <button type="button" disabled={resetLinkMutation.isPending} onClick={() => resetLinkMutation.mutate()} className={LINK_BUTTON}>
          {resetLinkMutation.isPending ? 'Generating…' : 'Send reset link'}
        </button>

        {isConfirmingRemove ? (
          <span className="flex items-center gap-2 ml-auto">
            <span className="text-[11px] text-coral font-bold">
              {isOnlyLogin ? 'This is the only login - nobody at this client could sign in. Remove anyway?' : 'Remove this operator?'}
            </span>
            <button type="button" disabled={removeMutation.isPending} onClick={() => removeMutation.mutate()} className="text-[11px] font-bold text-coral hover:text-coral/80 disabled:opacity-40">
              {removeMutation.isPending ? 'Removing…' : 'Confirm'}
            </button>
            <button type="button" onClick={() => setIsConfirmingRemove(false)} className="text-[11px] font-bold text-ink-mid hover:text-ink">
              Cancel
            </button>
          </span>
        ) : (
          <button type="button" onClick={() => setIsConfirmingRemove(true)} className="text-[11px] font-bold text-coral hover:text-coral/80 ml-auto">
            Remove operator
          </button>
        )}
      </div>
      <p className="text-[11px] text-ink-mid">
        Rotating replaces the password immediately - the current one stops working. A reset link lets the operator choose their own instead.
      </p>
      {failure && <span className="text-[11px] text-coral font-bold">{errorMessage(failure, 'That did not work')}</span>}
      {rotatedPassword && <SecretBanner label={PASSWORD_LABEL} value={rotatedPassword} onDismiss={() => setRotatedPassword(null)} />}
      {resetLink && (
        <SecretBanner label="Reset link — send it to this operator directly. It expires in 1 hour." value={resetLink} onDismiss={() => setResetLink(null)} />
      )}
    </div>
  )
}

/**
 * The people who can sign in to a client's dashboard with client-wide
 * access, and the admin's controls over those logins: add one (which is
 * what links a person to this client), edit, rotate the password, issue a
 * reset link, remove. Passwords are never readable - the API keeps only a
 * hash - so "does this operator have a password?" is always yes, and the
 * way to hand one over again is to rotate it or send a reset link.
 */
export function ClientOperators({ api, client }: { api: AdminApi; client: AdminClientSummary }) {
  const queryClient = useQueryClient()
  const { registrationId } = client
  const [isAdding, setIsAdding] = useState(false)
  const [openOperatorId, setOpenOperatorId] = useState<string | null>(null)
  const [invited, setInvited] = useState<InvitationResult | null>(null)
  const invitationsQuery = useQuery({
    queryKey: ['admin', 'operator-invitations', client.clientId],
    queryFn: () => api.listOperatorInvitations(client.clientId ?? ''),
    enabled: client.status === 'active' && Boolean(client.clientId),
  })
  const pendingFor = (operatorId: string): Invitation | undefined => invitationsQuery.data?.find((invitation) => invitation.subjectId === operatorId && invitation.status === 'pending')

  const operatorsQuery = useQuery({
    queryKey: ['admin', 'operators', registrationId],
    queryFn: () => api.listOperators(registrationId),
    enabled: client.status === 'active',
  })

  // The client list's contact name can come from an operator, so both are refreshed.
  async function refresh(): Promise<void> {
    await queryClient.invalidateQueries({ queryKey: ['admin', 'operators', registrationId] })
    await queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
    await queryClient.invalidateQueries({ queryKey: ['admin', 'operator-invitations', client.clientId] })
  }

  // The invite (invitations plan phase 2): the login is made at once, switched off; the person sets their own password.
  const createMutation = useMutation({
    mutationFn: (input: { email: string; firstName: string; lastName: string }) => api.inviteOperator(client.clientId ?? '', input),
    onSuccess: async (result) => {
      setInvited(result)
      setIsAdding(false)
      await refresh()
    },
  })
  const resendMutation = useMutation({ mutationFn: (invitationId: string) => api.resendOperatorInvitation(client.clientId ?? '', invitationId), onSuccess: async (result) => (setInvited(result), refresh()) })
  const revokeMutation = useMutation({ mutationFn: (invitationId: string) => api.revokeOperatorInvitation(client.clientId ?? '', invitationId), onSuccess: refresh })

  if (client.status !== 'active') {
    return <div className="text-xs text-ink-mid">Operators can be added once the client is active.</div>
  }
  if (operatorsQuery.isLoading) {
    return <Skeleton label="Loading operators" />
  }
  if (operatorsQuery.isError) {
    return <LoadFailure size="xs" message="Failed to load operators." onRetry={() => void operatorsQuery.refetch()} retrying={operatorsQuery.isFetching} />
  }

  const operators = operatorsQuery.data ?? []
  const hasLegacyLogin = Boolean(client.dashboardUsername)

  return (
    <div>
      {operators.length === 0 && !hasLegacyLogin && (
        <div className="text-xs text-coral font-bold mb-1">No operator logins - nobody at this client can sign in to the dashboard yet.</div>
      )}

      <ul className="text-xs text-ink space-y-1">
        {operators.map((operator) => {
          const isOpen = openOperatorId === operator.operatorId
          return (
            <li key={operator.operatorId}>
              <button
                type="button"
                aria-expanded={isOpen}
                onClick={() => setOpenOperatorId(isOpen ? null : operator.operatorId)}
                className="inline-flex items-center gap-1.5 font-bold text-marine hover:text-marine-dk underline decoration-dotted"
              >
                <OperatorIcon className="w-3.5 h-3.5" />
                {operator.firstName} {operator.lastName}
              </button>
              <span className="text-ink-mid"> · {operator.email}</span>
              {operator.invitationStatus === 'pending' && <span className="ml-1.5 rounded bg-gold/20 px-1.5 py-0.5 text-[10px] font-bold text-ink">Invited · waiting</span>}
              {isOpen && operator.invitationStatus === 'pending' ? (
                <PendingOperatorPanel
                  invitation={pendingFor(operator.operatorId) ?? null}
                  onResend={(invitationId) => resendMutation.mutate(invitationId)}
                  onRevoke={(invitationId) => revokeMutation.mutate(invitationId)}
                  isBusy={resendMutation.isPending || revokeMutation.isPending}
                  error={resendMutation.isError ? errorMessage(resendMutation.error, 'Could not resend') : revokeMutation.isError ? errorMessage(revokeMutation.error, 'Could not withdraw') : null}
                />
              ) : isOpen && (
                <ManageOperatorPanel
                  api={api}
                  registrationId={registrationId}
                  operator={operator}
                  isOnlyLogin={operators.length === 1 && !hasLegacyLogin}
                  onChanged={refresh}
                />
              )}
            </li>
          )
        })}
        {client.dashboardUsername && (
          <li>
            {client.dashboardUsername} <span className="text-ink-mid">· legacy dashboard login (not managed here)</span>
          </li>
        )}
      </ul>

      {invited && (
        <div className="mt-2">
          <InvitationHandOffBanner result={invited} onDismiss={() => setInvited(null)} />
        </div>
      )}

      {isAdding ? (
        <AddOperatorForm
          isPending={createMutation.isPending}
          error={createMutation.isError ? errorMessage(createMutation.error, 'Could not invite the operator') : null}
          onCreate={(input) => createMutation.mutate(input)}
          onCancel={() => {
            setIsAdding(false)
            createMutation.reset()
          }}
        />
      ) : (
        <div className="mt-2 flex items-center gap-3">
          <button type="button" onClick={() => setIsAdding(true)} className={LINK_BUTTON}>
            + Invite an operator
          </button>
          <RefreshButton
            size="sm"
            onRefresh={() => Promise.all([operatorsQuery.refetch(), invitationsQuery.refetch()])}
            refreshing={operatorsQuery.isFetching || invitationsQuery.isFetching}
          />
        </div>
      )}
    </div>
  )
}
